import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { XCircle, ArrowLeft, Loader2 } from 'lucide-react';
import Button from '../../components/ui/button';
import Header from '../../components/navbar/Header';
import Footer from '../../components/footer/footer';
import ClientPaymentModel from '../../model/client/client-payment-model';

/**
 * Page d'annulation de paiement
 * Affichée lorsque l'utilisateur annule le paiement Stripe
 */
const PaymentCancel: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';

  const [isLoading, setIsLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!paymentId) {
        setIsLoading(false);
        return;
      }

      try {
        const paymentResponse = await ClientPaymentModel.getInstance().getPaymentDetails(paymentId);
        if (paymentResponse.success && paymentResponse.data) {
          setPaymentDetails(paymentResponse.data);
        }
      } catch (err) {
        console.error('Error fetching payment details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [paymentId]);

  const handleBackToBooking = () => {
    // Rediriger vers la page de réservation du même établissement
    if (paymentDetails?.company) {
      const domain = paymentDetails.company.domain;
      const businessName = paymentDetails.company.businessName;
      navigate(`/${currentLang}/${domain}/${businessName}`);
    } else {
      // Fallback vers l'accueil si les infos ne sont pas disponibles
      navigate(`/${currentLang}`);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50 pt-20 pb-16">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {isLoading ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('payment.loading', 'Chargement...')}
              </h2>
              <p className="text-gray-600">
                {t('payment.pleaseWait', 'Veuillez patienter')}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {/* En-tête annulation */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('payment.cancelTitle', 'Paiement annulé')}
                </h1>
                <p className="text-lg text-gray-600">
                  {t('payment.cancelMessage', 'Vous avez annulé le processus de paiement')}
                </p>
              </div>

              {/* Message d'information */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('payment.whatHappensNow', 'Que se passe-t-il maintenant ?')}
                </h3>
                <ul className="space-y-2 text-sm text-gray-800">
                  <li className="flex items-start">
                    <span className="text-gray-600 mr-2">•</span>
                    <span>{t('payment.cancelInfo1', 'Votre réservation n\'a pas été confirmée')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-600 mr-2">•</span>
                    <span>{t('payment.cancelInfo2', 'Aucun montant n\'a été débité de votre compte')}</span>
                  </li>
                </ul>
              </div>

              {/* Message de support */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  {t('payment.needHelp', 'Besoin d\'aide ? Contactez-nous si vous rencontrez des difficultés lors du processus de paiement.')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-center">
                <Button
                  onClick={handleBackToBooking}
                  className="bg-primary hover:bg-primary-dark px-8"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('payment.backToBooking', 'Page de réservation')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentCancel;
